import { useState, useCallback } from 'react';

import { Box, Flex, Skeleton } from '@chakra-ui/react';
import { pdf } from '@react-pdf/renderer';
import { STATIC_COHORTS } from 'Deer/constants/cohorts';
import { useNotifyPeople } from 'Deer/pages/Company/DisciplinaryActions/dataHooks';
import {
  usePublishDisciplinaryNote,
  useInvalidateGetDisciplinaryActions,
  useDeleteViolationNote,
} from 'Deer/pages/MyData/DisciplinaryActions/dataHooks';
import { MY_DATA, MY_ORGANIZATION, MY_TEAM } from 'Deer/pages/Team/DirectReports/DisciplinaryActions/constants';
import { useQuery as useQueryParams } from 'components/hooks/useQuery';
import { useToast } from 'components/hooks/useToast';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';

import { TOAST_STATUSES } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';
import { routes } from 'constants/routes';

import { BottomDrawer, DrawerContent, DrawerHeader } from 'uiToolkitV1/BottomDrawer';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { PdfGeneration } from './components/PdfGeneration';
import { ViewViolationAdminViewFooter } from './components/ViewViolationAdminViewFooter';
import { ViewViolationApproval } from './components/ViewViolationApproval';
import { ViewViolationCommentModal } from './components/ViewViolationCommentModal';
import { ViewViolationDetails } from './components/ViewViolationDetails';
import { ViewViolationEmployeeViewFooter } from './components/ViewViolationEmployeeViewFooter';
import { ViewViolationHeader } from './components/ViewViolationHeader';
import { ViewViolationManagerViewFooter } from './components/ViewViolationManagerViewFooter';
import { useEvaluateNote, useGetViolationNote } from './dataHooks';
import { VIOLATION_STATUS } from './types';

export const ViewViolationDrawer = () => {
  const history = useHistory();
  const location = useLocation();
  const selectedCohort = useSelectedView();
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const toast = useToast();

  const { viewViolation, violationNoteId } = useQueryParams();
  const isBottomDrawerOpen = viewViolation === 'true';

  // Ensure violationNoteId is a string
  const noteId = Array.isArray(violationNoteId) ? violationNoteId[0] : violationNoteId;

  const { mutate: publishNote } = usePublishDisciplinaryNote();
  const { mutate: notifyPeople } = useNotifyPeople();
  const { mutate: deleteNote } = useDeleteViolationNote();

  const invalidateDisciplinaryActions = useInvalidateGetDisciplinaryActions();

  const {
    data: violationNote,
    isLoading: isViolationNoteLoading,
    isError: isViolationNoteError,
  } = useGetViolationNote({ noteId: Number(violationNoteId), enabled: isBottomDrawerOpen });

  const { mutate: evaluateNote, isLoading: isEvaluateNoteLoading } = useEvaluateNote();

  const handleBottomDrawerClose = useCallback(() => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.delete('viewViolation');
    queryParams.delete('violationNoteId');
    history.push({ search: queryParams.toString() });
  }, [history, location.search]);

  const generatePdfFormData = async () => {
    try {
      const blob = await pdf(<PdfGeneration violationNote={violationNote} />).toBlob();

      const formData = new FormData();
      formData.append('file', blob, 'document.pdf'); // 'file' is the field name expected by backend
      formData.append('returnUrl', `${window.location.origin}${routes.staticDisciplinaryActionsESignSuccess}`);
      return formData;
    } catch (error) {
      console.error('Error generating PDF:', error);
      return null;
    }
  };

  const handlePublish = useCallback(() => {
    if (!noteId) {
      console.error('No noteId provided for publishing disciplinary action');
      return;
    }

    publishNote(
      { noteId: parseInt(noteId, 10) },
      {
        onSuccess: () => {
          toast({
            title: 'Disciplinary action published successfully',
            status: TOAST_STATUSES.SUCCESS,
          });
          invalidateDisciplinaryActions();
          handleBottomDrawerClose();
        },
        onError: (err) => {
          console.error('Error publishing disciplinary note', err);
          toast({
            title: 'Failed to publish disciplinary action',
            status: TOAST_STATUSES.ERROR,
          });
        },
      }
    );
  }, [noteId, publishNote, invalidateDisciplinaryActions, toast, handleBottomDrawerClose]);

  const handleNotify = useCallback(() => {
    notifyPeople([+noteId], {
      onSuccess: () => {
        toast({
          title: 'Notified successfully',
          status: 'success',
        });
      },
      onError: (err) => {
        console.error(`Error sending notification`, err);
        toast({
          title: 'Error sending notification',
          status: 'error',
        });
      },
    });
  }, [noteId, notifyPeople, toast]);

  const handleDelete = useCallback(() => {
    if (!violationNote?.id) {
      console.error('No noteId provided for deleting the disciplinary action');
      return;
    }

    deleteNote(
      { noteId: +noteId },
      {
        onSuccess: () => {
          invalidateDisciplinaryActions();
          toast({
            title: 'Disciplinary note deleted successfully',
            status: 'success',
          });
          handleBottomDrawerClose();
        },
        onError: (err) => {
          console.error('Error deleting disciplinary note', err);
          toast({
            title: 'Error deleting disciplinary note',
            status: 'error',
          });
        },
      }
    );
  }, [deleteNote, handleBottomDrawerClose, invalidateDisciplinaryActions, noteId, toast, violationNote?.id]);

  if (isViolationNoteLoading) {
    return (
      <BottomDrawer isOpen={isBottomDrawerOpen} onClose={handleBottomDrawerClose}>
        <DrawerContent p="4px" mr={isMobileView() ? 'auto !important' : undefined}>
          <DrawerHeader onClose={handleBottomDrawerClose}>{undefined}</DrawerHeader>
          <Box
            display="flex"
            flexDir="column"
            h="full"
            border="4px solid"
            borderColor="border.EBEFF3"
            borderRadius="8px"
            p="8px"
            gap="12px"
          >
            <Skeleton h="112px" w="full" borderRadius="8px" />
            <Skeleton h="400px" w="full" borderRadius="8px" />
            <Skeleton h="200px" w="full" borderRadius="8px" />
          </Box>
        </DrawerContent>
      </BottomDrawer>
    );
  }

  if (isViolationNoteError) {
    return (
      <BottomDrawer isOpen={isBottomDrawerOpen} onClose={handleBottomDrawerClose}>
        <DrawerContent p="4px" mr={isMobileView() ? 'auto !important' : undefined}>
          <DrawerHeader onClose={handleBottomDrawerClose}>{undefined}</DrawerHeader>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            h="full"
            border="4px solid"
            borderColor="border.EBEFF3"
            borderRadius="8px"
            p="8px"
          >
            <TextBodyMedium>There was an error loading disciplinary action.</TextBodyMedium>
          </Box>
        </DrawerContent>
      </BottomDrawer>
    );
  }

  const isEmployeeView = selectedCohort?.id === MY_DATA.id;
  const isManagerView = selectedCohort?.id === MY_TEAM.id || selectedCohort?.id === MY_ORGANIZATION.id;
  const isAdminView = selectedCohort?.id === STATIC_COHORTS.company.id;

  const hasPassedApprovalStage =
    violationNote.status === VIOLATION_STATUS.APPROVED ||
    violationNote.status === VIOLATION_STATUS.PUBLISHED_TO_EMPLOYEE ||
    violationNote.status === VIOLATION_STATUS.ACKNOWLEDGED;

  if (isCommentModalOpen) {
    return (
      <ViewViolationCommentModal
        isOpen={isCommentModalOpen}
        onClose={() => setIsCommentModalOpen(false)}
        isUpdating={isEvaluateNoteLoading}
        onSave={(comment) => {
          evaluateNote(
            {
              noteId: violationNote.id,
              body: {
                reason: comment,
                evaluation: 'DENIED',
              },
            },
            {
              onSuccess: () => {
                setIsCommentModalOpen(false);
                handleBottomDrawerClose();
              },
            }
          );
        }}
      />
    );
  }

  return (
    <BottomDrawer isOpen={isBottomDrawerOpen} onClose={handleBottomDrawerClose}>
      <DrawerContent p="4px" mr={isMobileView() ? 'auto !important' : undefined}>
        <DrawerHeader onClose={handleBottomDrawerClose}>{undefined}</DrawerHeader>
        <Flex flexDir="column" h="full" border="4px solid" borderColor="border.EBEFF3" borderRadius="8px">
          <Flex flex="1" minH="0" flexDir="column" p="8px" gap="12px">
            <ViewViolationHeader violationStatus={violationNote.status} isEmployeeView={isEmployeeView} />
            <Flex flex="1" flexDir="column" gap="12px" overflowY="auto">
              <ViewViolationDetails
                isEmployeeView={isEmployeeView}
                raisedFor={violationNote.raisedFor.displayName}
                raisedForAvatar={violationNote.raisedFor.profilePhoto}
                type={violationNote.type}
                severity={violationNote.severity}
                raisedBy={violationNote.raisedBy.displayName}
                raisedByAvatar={violationNote.raisedBy.profilePhoto}
                raisedByRole={violationNote.raisedBy.designation}
                reportedTime={violationNote.reportedTime}
                description={violationNote.descriptionHtml}
              />
              {/* DON'T SHOW ADMIN COMMENT ON EMPLOYEE VIEW */}
              {!isEmployeeView &&
                (hasPassedApprovalStage || violationNote.status === VIOLATION_STATUS.DENIED) &&
                violationNote.evaluatedBy && (
                  <ViewViolationApproval
                    isApproved={hasPassedApprovalStage}
                    commentText={violationNote.reason}
                    evaluatedBy={violationNote.evaluatedBy.displayName}
                    evaluatedByAvatar={violationNote.evaluatedBy.profilePhoto}
                  />
                )}
            </Flex>
          </Flex>
          {isAdminView && (
            <ViewViolationAdminViewFooter
              violationNoteId={violationNote.id}
              violationStatus={violationNote.status}
              isPublishAllowed={violationNote.publishToEmployeeAllowed}
              onCancel={handleBottomDrawerClose}
              onDeny={() => setIsCommentModalOpen(true)}
              onPublish={handlePublish}
            />
          )}
          {isManagerView && (
            <ViewViolationManagerViewFooter
              violationStatus={violationNote.status}
              onCancel={handleBottomDrawerClose}
              onPublish={handlePublish}
              onNotify={handleNotify}
              isPublishAllowed={violationNote.publishToEmployeeAllowed}
              onDelete={handleDelete}
            />
          )}
          {isEmployeeView && (
            <ViewViolationEmployeeViewFooter
              violationNoteId={violationNote.id}
              violationStatus={violationNote.status}
              onCancel={handleBottomDrawerClose}
              generatePdfFormData={generatePdfFormData}
            />
          )}
        </Flex>
      </DrawerContent>
    </BottomDrawer>
  );
};
